import { config } from "./config";
import { Feature, View, Map } from "ol";
import { Vector as VectorSource, TileWMS, XYZ, ImageArcGISRest } from "ol/source";
import { Tile as TileLayer, Vector as VectorLayer, Image as ImageLayer } from "ol/layer";
import TileGrid from "ol/tilegrid/TileGrid";
import { defaults as defaultControls } from "ol/control";
import { Basemaps } from "./enums";
import { getCenter, containsExtent } from "ol/extent";
import { ScaleControl } from "./Buttons/Scale";
import { AttributionButton } from "./Buttons/Attribution";
import { drawCanvasAttribution, drawCanvasMap, drawCanvasScale } from "./canvasUtils";
import "./snapshots.scss";

const shadowMapId = "shadow-map";
const shadowOverviewId = "shadow-map-overview";

export const generateSnapshot = (features: Feature[], basemap: Basemaps): Promise<string> => {
    const source = new VectorSource({ features });
    const sourceExtent = source.getExtent();

    const shadowMap = createShadowMap(basemap, source, getCenter(sourceExtent));

    return new Promise((resolve) => {
        shadowMap.once("rendercomplete", () => {
            const viewExtent = shadowMap.getView().calculateExtent();

            if (containsExtent(viewExtent, sourceExtent)) {
                const snapshot = getSnapshot(shadowMap, true, true);
                document.getElementById(shadowMapId).remove();
                resolve(snapshot);
            } else {
                shadowMap.getView().fit(source.getExtent(), {
                    padding: [50, 50, 50, 50],
                    callback: () =>
                        shadowMap.once("rendercomplete", () => {
                            const snapshot = getSnapshot(shadowMap, true, true);
                            document.getElementById(shadowMapId).remove();
                            resolve(snapshot);
                        })
                });
            }
        });
    });
};
export const generateOverview = (features: Feature[], basemap: Basemaps): Promise<string> => {
    const source = new VectorSource({ features });
    const sourceExtent = source.getExtent();

    const shadowMap = createShadowMap(basemap, source, getCenter(sourceExtent), true);

    return new Promise((resolve) => {
        shadowMap.once("rendercomplete", () => {
            setTimeout(() => {
                const snapshot = getSnapshot(shadowMap);
                document.getElementById(shadowOverviewId).remove();
                resolve(snapshot);
            }, 1000);
        });
    });
};

const createShadowMap = (basemapId: Basemaps, source: VectorSource, center, isOverview?: boolean) => {
    const target = document.createElement("div");
    target.id = isOverview ? shadowOverviewId : shadowMapId;
    document.body.append(target);

    const layers = [createBasemapLayer(basemapId)];
    !isOverview &&
        config.layers.map((layer) =>
            layers.push(
                new ImageLayer({
                    id: layer.id,
                    source: new ImageArcGISRest({
                        url: layer.url,
                        crossOrigin: layer.crossOrigin,
                        attributions: layer.copyright
                    })
                })
            )
        );
    layers.push(createDrawingLayer(source));

    const controls = defaultControls({ rotate: false, attribution: false }).extend([
        new AttributionButton(),
        new ScaleControl()
    ]);

    const view = isOverview
        ? new View({
              projection: config.default.projection,
              center: config.default.center,
              zoom: 2.6
          })
        : new View({
              projection: config.default.projection,
              center,
              resolution: 1
          });

    return new Map({ layers, target, view, controls });
};

const createBasemapLayer = (basemapId: Basemaps) =>
    basemapId === Basemaps.Orto
        ? new TileLayer({
              source: new TileWMS({
                  url: config.basemapOrto.source.url,
                  params: config.basemapOrto.source.params,
                  projection: config.basemapOrto.source.projection,
                  serverType: config.basemapOrto.source.serverType,
                  crossOrigin: config.basemapOrto.source.crossOrigin
              })
          })
        : new TileLayer({
              source: new XYZ({
                  url: config.basemapLVM.source.url,
                  maxZoom: config.basemapLVM.source.maxZoom,
                  projection: config.basemapLVM.source.projection,
                  tileGrid: new TileGrid(config.basemapLVM.tileGrid),
                  crossOrigin: config.basemapLVM.source.crossOrigin
              })
          });

const createDrawingLayer = (source: VectorSource) =>
    new VectorLayer({
        id: "shadow-drawing-layer",
        source
    });

const getSnapshot = (shadowMap: Map, showScale?: boolean, showAttribution?: boolean) => {
    const mapCanvas = drawCanvasMap(shadowMap);

    showScale && drawCanvasScale(mapCanvas);
    showAttribution && drawCanvasAttribution(mapCanvas);

    return mapCanvas.toDataURL();
};
